import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LegalDocument } from "@/components/LegalDocument";
import { toast } from "sonner";
import FullScreenLoader from "@/components/FullScreenLoader";
import { FileText, Eye, Save, Send, RotateCcw } from "lucide-react";

export default function PlatformLegal() {
  const [docs, setDocs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null); // {title, content, effective_date}
  const [preview, setPreview] = useState(null);
  const [publicPreview, setPublicPreview] = useState(null);

  const load = () => api.get("/platform/legal").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  if (!docs) return <FullScreenLoader />;

  const openEditor = async (docType) => {
    const grp = docs.find((d) => d.doc_type === docType);
    const draft = grp.versions.find((v) => v.status === "draft");
    const active = grp.versions.find((v) => v.status === "published");
    const src = draft || active || grp.versions[0];
    const full = await api.get(`/platform/legal/${src.id}`);
    setSelected(grp);
    setEditor({ id: draft ? draft.id : null, doc_type: docType, title: full.data.title, content: full.data.content, effective_date: full.data.effective_date, is_agreement: full.data.is_agreement });
  };

  const saveDraft = async () => {
    await api.post(`/platform/legal/${editor.doc_type}/draft`, {
      title: editor.title, content: editor.content, effective_date: editor.effective_date, is_agreement: editor.is_agreement,
    });
    toast.success("Draft saved");
    setEditor(null); load();
  };

  const publishDraft = async (docType) => {
    const grp = docs.find((d) => d.doc_type === docType);
    const draft = grp.versions.find((v) => v.status === "draft");
    if (!draft) { toast.error("Save a draft first"); return; }
    await api.post(`/platform/legal/${draft.id}/publish`);
    toast.success("New version published. Merchants will be asked to re-accept.");
    load();
  };

  const restore = async (versionId) => {
    if (!window.confirm("Restore this version as a new published version?")) return;
    await api.post(`/platform/legal/${versionId}/restore`);
    toast.success("Version restored & published");
    load();
  };

  const showPublicPreview = async (id) => {
    const full = await api.get(`/platform/legal/${id}`);
    setPublicPreview(full.data);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Legal Documents</h1>
        <p className="text-muted-foreground mt-1">Edit, version, and publish platform legal documents.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {docs.map((d) => {
          const hasDraft = d.versions.some((v) => v.status === "draft");
          return (
            <Card key={d.doc_type} className="rounded-xl border-border p-5" data-testid={`legal-doc-card-${d.doc_type}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center"><FileText className="h-4 w-4 text-primary" /></div>
                  <div>
                    <h3 className="font-semibold">{d.title}</h3>
                    <p className="text-xs text-muted-foreground">Active v{d.active_version ?? "—"}{d.is_agreement ? " · Requires acceptance" : ""}</p>
                  </div>
                </div>
                {hasDraft && <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-0 rounded-full">Draft</Badge>}
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => openEditor(d.doc_type)} data-testid={`edit-${d.doc_type}`}><Save className="h-3.5 w-3.5 mr-1.5" /> Edit / Draft</Button>
                {hasDraft && <Button size="sm" onClick={() => publishDraft(d.doc_type)} data-testid={`publish-${d.doc_type}`}><Send className="h-3.5 w-3.5 mr-1.5" /> Publish</Button>}
              </div>

              <div className="mt-4 border-t border-border pt-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Version history</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {d.versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">v{v.version}
                        <Badge variant="secondary" className={`border-0 rounded-full text-xs ${v.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{v.status}</Badge>
                      </span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => showPublicPreview(v.id)} className="p-1 text-muted-foreground hover:text-foreground" title="Preview"><Eye className="h-4 w-4" /></button>
                        {v.status === "published" && v.version !== d.active_version && (
                          <button onClick={() => restore(v.id)} className="p-1 text-muted-foreground hover:text-primary" title="Restore"><RotateCcw className="h-4 w-4" /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Editor */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit — {editor?.title}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><label className="text-sm font-medium">Title</label><Input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} data-testid="editor-title" /></div>
                <div className="space-y-1.5"><label className="text-sm font-medium">Effective date</label><Input value={editor.effective_date || ""} onChange={(e) => setEditor({ ...editor, effective_date: e.target.value })} placeholder="2026-07-27" /></div>
              </div>
              <div className="space-y-1.5"><label className="text-sm font-medium">Content (use ## for headings)</label><Textarea value={editor.content} onChange={(e) => setEditor({ ...editor, content: e.target.value })} rows={16} className="font-mono text-xs" data-testid="editor-content" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPreview(editor)} data-testid="editor-preview-btn"><Eye className="h-4 w-4 mr-1.5" /> Preview</Button>
                <Button onClick={saveDraft} data-testid="editor-save-btn"><Save className="h-4 w-4 mr-1.5" /> Save draft</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Draft preview (from editor) */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="sr-only">Preview</DialogTitle></DialogHeader>
          {preview && (
            <article>
              <h1 className="text-2xl font-bold mb-4">{preview.title}</h1>
              {preview.content.split("\n\n").map((b, i) => b.startsWith("## ")
                ? <h2 key={i} className="text-lg font-bold mt-6 mb-2">{b.replace("## ", "")}</h2>
                : <p key={i} className="text-sm text-foreground/80 mb-3 whitespace-pre-wrap">{b}</p>)}
            </article>
          )}
        </DialogContent>
      </Dialog>

      {/* Version preview */}
      <Dialog open={!!publicPreview} onOpenChange={(o) => !o && setPublicPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="sr-only">{publicPreview?.title}</DialogTitle></DialogHeader>
          {publicPreview && (
            <article>
              <h1 className="text-2xl font-bold mb-1">{publicPreview.title}</h1>
              <p className="text-xs text-muted-foreground mb-4">v{publicPreview.version} · {publicPreview.status}</p>
              {publicPreview.content.split("\n\n").map((b, i) => b.startsWith("## ")
                ? <h2 key={i} className="text-lg font-bold mt-6 mb-2">{b.replace("## ", "")}</h2>
                : <p key={i} className="text-sm text-foreground/80 mb-3 whitespace-pre-wrap">{b}</p>)}
            </article>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
